/**
 * Minimal, dependency-free, XSS-safe Markdown → React renderer for NORA's
 * assistant bubbles.
 *
 * Why hand-rolled: no markdown library is available in this project and it
 * cannot be added here. More importantly, this builds React ELEMENTS directly
 * and never uses dangerouslySetInnerHTML — so untrusted content that reaches a
 * bubble (e.g. a station name from an upstream feed) cannot inject markup. It
 * covers exactly the subset NORA emits: paragraphs, **bold**, `inline code`,
 * bullet/numbered lists, and GitHub-style pipe tables (the big readability win
 * for fleet stats). Anything it doesn't recognize renders as plain text.
 *
 * Deliberately NOT supported (and rendered literally): raw HTML, images, links
 * (NORA's navigation goes through the separate ```links block, not markdown),
 * headings, blockquotes. Keeping the grammar tiny keeps it safe and predictable.
 */
import { Fragment, type ReactNode } from 'react';

/** Parse inline **bold** and `code` within a single line into React nodes. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Split on **bold** and `code`, keeping the delimiters via capture groups.
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  parts.forEach((part, i) => {
    if (!part) return;
    if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
      nodes.push(<strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>);
    } else if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
      nodes.push(
        <code key={`${keyPrefix}-c${i}`} className="chat-md-code">
          {part.slice(1, -1)}
        </code>
      );
    } else {
      nodes.push(<Fragment key={`${keyPrefix}-t${i}`}>{part}</Fragment>);
    }
  });
  return nodes;
}

/** A markdown pipe-table row → trimmed cell strings. `| a | b |` → ['a','b']. */
function splitTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
}

/** True for a table separator row like `| --- | :---: |` or `| - | - |`. */
function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line);
}

/**
 * Render a Markdown string to React nodes. Block-level first (tables, lists,
 * paragraphs), then inline within each block.
 */
export function renderMarkdown(md: string): ReactNode {
  if (typeof md !== 'string' || md.trim() === '') return null;
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line → block separator.
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Table: a header row followed by a separator row.
    if (line.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitTableRow(line);
      const rows: string[][] = [];
      i += 2; // skip header + separator
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        rows.push(splitTableRow(lines[i]));
        i++;
      }
      blocks.push(
        <table key={`tbl${key++}`} className="chat-md-table">
          <thead>
            <tr>
              {header.map((h, hi) => (
                <th key={hi}>{renderInline(h, `h${key}-${hi}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>
                {r.map((c, ci) => (
                  <td key={ci}>{renderInline(c, `r${key}-${ri}-${ci}`)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
      continue;
    }

    // Unordered list: consecutive lines starting with -, *, or •.
    if (/^\s*[-*•]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*•]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={`ul${key++}`} className="chat-md-list">
          {items.map((it, ii) => (
            <li key={ii}>{renderInline(it, `ul${key}-${ii}`)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list: consecutive lines starting with `N.`.
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={`ol${key++}`} className="chat-md-list">
          {items.map((it, ii) => (
            <li key={ii}>{renderInline(it, `ol${key}-${ii}`)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Paragraph: gather consecutive non-blank, non-structural lines.
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^\s*[-*•]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !(lines[i].includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1]))
    ) {
      para.push(lines[i]);
      i++;
    }
    if (para.length > 0) {
      const pKey = key++;
      blocks.push(
        <p key={`p${pKey}`} className="chat-md-p">
          {para.map((pl, pi) => (
            <Fragment key={pi}>
              {pi > 0 && <br />}
              {renderInline(pl, `p${pKey}-${pi}`)}
            </Fragment>
          ))}
        </p>
      );
    }
  }

  return blocks;
}
